import { queryOptions, useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { subscriptionApi } from '@/shared/api';
import type {
  ActiveProviderSubscription,
  CreateSubscriptionCheckoutResponse,
  GetSubscriptionCheckoutStatusResponse,
  GetSubscriptionInvoiceResponse,
} from '@/shared/api';
import { subscriptionKeys } from '@/shared/lib/queryKeys';

export { subscriptionKeys };

const POLLING_REQUEST_TIMEOUT_MS = 3000;

export const subscriptionQueryOptions = {
  active: (timeoutMs?: number) =>
    queryOptions<ActiveProviderSubscription | null>({
      queryKey: subscriptionKeys.active(),
      queryFn: ({ signal }) => subscriptionApi.getActive({ signal, timeoutMs }),
      meta: { errorToast: false },
    }),
  invoice: (checkoutId: string) =>
    queryOptions<GetSubscriptionInvoiceResponse | null>({
      queryKey: subscriptionKeys.invoice(checkoutId),
      queryFn: ({ signal }) =>
        subscriptionApi.getInvoice(checkoutId, {
          signal,
          timeoutMs: POLLING_REQUEST_TIMEOUT_MS,
        }),
      staleTime: 0,
      retry: false,
      meta: { errorToast: false },
    }),
  checkoutStatus: (checkoutId: string) =>
    queryOptions<GetSubscriptionCheckoutStatusResponse>({
      queryKey: subscriptionKeys.checkoutStatus(checkoutId),
      queryFn: ({ signal }) =>
        subscriptionApi.getCheckoutStatus(checkoutId, {
          signal,
          timeoutMs: POLLING_REQUEST_TIMEOUT_MS,
        }),
      staleTime: 0,
      retry: false,
      meta: { errorToast: false },
    }),
};

export { POLLING_REQUEST_TIMEOUT_MS };

export function useCreateSubscriptionCheckout(
  options?: UseMutationOptions<CreateSubscriptionCheckoutResponse, Error, void, unknown>
) {
  return useMutation({
    ...options,
    meta: {
      errorToast: 'Failed to start payment. Contact support.',
      ...options?.meta,
    },
    mutationFn: () => subscriptionApi.createCheckout(),
  });
}
